import React, {useContext, useEffect, useRef, useState} from "react";
import {HeadCell} from "../../../../graphics/tables/data/HeadCell";
import {TableRowData} from "../../../../graphics/tables/data/TableRowData";
import {DynamicDatatable, EditOptionType} from "../../../../graphics/tables/DynamicDatatable";
import {Order} from "../../../../graphics/tables/operations/sort";
import {strings} from "../../i18n/strings";
import {Page} from "./index";
import {AppContext, AppContextSpec, useAsyncEffect} from "../../App";
import {useOktaAuth} from "../../../../okta-react";
import {TransferTransporterByCountryTableStructure} from "../../domain/indexes/TransferTransporterByCountryIndexes";
import {EditForm, EditFormModalOptions} from "../../domain/forms/TransferTransporterByCountryEditForm";
import {
    deleteTransferTransporterByCountry,
    getTransferTransporterByCountryColumnInformation,
    searchTransferTransporterByCountry
} from "../../domain/repository/TransferTransporterByCountryRepository";
import {Constraints} from "../../domain/indexes/Indexes";

export type Record = TransferTransporterByCountryTableStructure.RecordOf<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>;
export interface RecordRepresentation extends Record, TableRowData {}
export enum EditMode { Add, Edit, Duplicate }
export interface RecordData {
    record: RecordRepresentation;
    type: EditMode;
}

type TransferField = "countryCode" | "defaultTransporter" | "transporterCode" | "daysOff";
const tableFields: TransferField[] = [
    "countryCode",
    "transporterCode",
    "defaultTransporter",
    "daysOff"
];
const pageWording = strings.page.transferTransporterByCountry;
const isDefault = (value: unknown) => String(value).toUpperCase() === "Y";

const makeConstraint = (
    name: string,
    displayName: string,
    type: string,
    required: boolean,
    position: number,
    reference = ""
): Constraints => ({
    id: name,
    name,
    displayName,
    type,
    required,
    information: "",
    position,
    visible: true,
    editable: true,
    massEditable: false,
    reference,
    referenceColumnLabel: ""
});

const columnConstraints: TransferTransporterByCountryTableStructure.TransferTransporterByCountry = {
    id: makeConstraint("id", "Id", "string", false, 0),
    countryCode: makeConstraint("countryCode", "Country Code", "string", true, 1, "country"),
    defaultTransporter: makeConstraint("defaultTransporter", "Default", "boolean", true, 2),
    transporterCode: makeConstraint("transporterCode", "Transporter Code", "string", true, 3, "transporterDaysOffWeek"),
    daysOff: makeConstraint("daysOff", "Days Off", "date", false, 4),
    default: {
        id: "",
        type: "",
        required: false,
        information: "",
        position: 0,
        name: "",
        displayName: "",
        visible: false,
        editable: false,
        massEditable: false,
        reference: "",
        referenceColumnLabel: ""
    }
};

const initialHeadCells: HeadCell<RecordRepresentation>[] = tableFields.map((field) => {
    const config = columnConstraints[field];
    return {
        id: config.name as keyof RecordRepresentation & string,
        label: config.name,
        displayName: config.displayName,
        type: config.type,
        required: config.required,
        information: config.information,
        position: config.position,
        visible: config.visible,
        editable: config.editable,
        massEditable: config.massEditable,
        reference: config.reference,
        referenceColumnLabel: config.referenceColumnLabel,
        disablePadding: false,
        searchValue: ""
    };
});

export const TransferTransporterByCountryPage = () => {
    const defaultLimit = process.env.REACT_APP_DEFAULT_PAGINATION_LIMIT
        ? parseInt(process.env.REACT_APP_DEFAULT_PAGINATION_LIMIT)
        : 20;
    const [headCells, setHeadCells] = useState<HeadCell<RecordRepresentation>[]>(initialHeadCells);
    const [rows, setRows] = useState<RecordRepresentation[]>([]);
    const [tableData, setTableData] = useState<TransferTransporterByCountryTableStructure.ResponseOf<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>>();
    const [constraints, setConstraints] = useState<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>(columnConstraints);
    const [recordToEdit, setRecordToEdit] = useState<RecordData>();
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(defaultLimit);
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<string>(tableFields[0]);
    const {authState} = useOktaAuth();
    const appContext = useContext<AppContextSpec>(AppContext);
    const notifyErrorRef = useRef(appContext.notifyError);
    notifyErrorRef.current = appContext.notifyError;

    useEffect(() => {
        if (!authState) return;

        const loadColumnInformation = async () => {
            try {
                const definition = await getTransferTransporterByCountryColumnInformation<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>(authState);
                const mergedDefinition = {
                    ...columnConstraints,
                    ...definition,
                    defaultTransporter: definition.defaultTransporter || columnConstraints.defaultTransporter
                };
                setConstraints(mergedDefinition);
                setHeadCells(tableFields.map((field) => {
                    const config = mergedDefinition[field];
                    return {
                        id: config.name as keyof RecordRepresentation & string,
                        label: config.name,
                        displayName: config.displayName || config.name,
                        type: config.type,
                        required: config.required,
                        information: config.information,
                        position: config.position,
                        visible: config.visible,
                        editable: config.editable,
                        massEditable: config.massEditable,
                        reference: config.reference,
                        referenceColumnLabel: config.referenceColumnLabel,
                        disablePadding: false,
                        searchValue: ""
                    };
                }));
            } catch (error: any) {
                console.error("Failed to load transfer transporter column information", error);
                notifyErrorRef.current(error.message || "Failed to load transfer transporter column information");
            }
        };

        void loadColumnInformation();
    }, [authState]);

    const refreshRecords = (searchCells = headCells) => {
        const searches = searchCells.filter(cell => cell.label && cell.searchValue)
            .map(cell => ({key: cell.label, value: cell.searchValue}));
        (async () => {
            try {
                const result = await searchTransferTransporterByCountry<Record>(
                    authState!, searches, page, limit, order, orderBy
                );
                setTableData(result);
                setRows(result.values);
                if (page !== result.pagination.page) setPage(result.pagination.page);
            } catch (error: any) {
                console.error("Failed to load transfer transporter configuration", error);
                appContext.notifyError(error.message || "Failed to load transfer transporter configuration");
            }
        })();
    };

    useAsyncEffect(async () => {
        if (headCells.length) refreshRecords();
    }, [page, limit, order, orderBy, headCells.length]);

    const openForm = (record?: RecordData) => {
        setRecordToEdit(record);
        setModalOpen(true);
    };

    const closeForm = () => {
        setModalOpen(false);
        setRecordToEdit(undefined);
    };

    const deleteRow = (row: RecordRepresentation) => {
        if (isDefault(row.defaultTransporter)) {
            appContext.notifyError(`You should set another default transporter for ${row.countryCode} before removing this one.`);
            return;
        }
        appContext.prompt(pageWording.datatable.action.remove.confirmationPromptMessage, async confirmed => {
            if (!confirmed) return;
            try {
                appContext.displayLoader(true);
                await deleteTransferTransporterByCountry(authState!, String(row.id));
                refreshRecords();
                appContext.notifySuccess(pageWording.datatable.action.remove.confirmedFeedbackMessage);
            } catch (error: any) {
                appContext.notifyError(error.message || "Failed to delete transfer transporter");
            } finally {
                appContext.displayLoader(false);
            }
        }, {confirmButtonColor: "secondary"});
    };

    const editOptions = [
        {id: EditOptionType.Edit, name: pageWording.datatable.action.edit.menuItemLabel, onClick: (_option: EditOptionType, row: RecordRepresentation) => openForm({record: row, type: EditMode.Edit})},
        {id: EditOptionType.Duplicate, name: pageWording.datatable.action.duplicate.menuItemLabel, onClick: (_option: EditOptionType, row: RecordRepresentation) => openForm({record: row, type: EditMode.Duplicate})},
        {id: EditOptionType.Delete, name: pageWording.datatable.action.remove.menuItemLabel, onClick: (_option: EditOptionType, row: RecordRepresentation) => deleteRow(row)}
    ];

    const modalOptions: EditFormModalOptions = {
        title: recordToEdit
            ? recordToEdit.type === EditMode.Duplicate ? pageWording.datatable.action.duplicate.modal.title : pageWording.datatable.action.edit.modal.title
            : pageWording.datatable.action.add.modal.title,
        applyLabel: pageWording.datatable.action.add.modal.applyButtonLabel,
        cancelLabel: pageWording.datatable.action.add.modal.cancelButtonLabel,
        open: modalOpen,
        onClose: closeForm,
        onSuccess: async () => {
            refreshRecords();
            appContext.notifySuccess(recordToEdit
                ? recordToEdit.type === EditMode.Duplicate
                    ? pageWording.datatable.action.duplicate.confirmationFeedbackMessage
                    : pageWording.datatable.action.edit.confirmationFeedbackMessage
                : pageWording.datatable.action.add.confirmationFeedbackMessage);
            closeForm();
        }
    };

    return (
        <Page title={pageWording.title} descriptionLine1={pageWording.description.line1} descriptionLine2="">
            <DynamicDatatable
                customEditOption
                shouldHideForRegion
                stickyHeader
                headCells={headCells}
                data={rows}
                initialOrderBy={tableFields[0]}
                showPadding={false}
                editOptions={editOptions}
                allowAdding={(appContext.appInfo?.scopes.includes("WRITE_ALL") || appContext.appInfo?.scopes.includes("HQ"))
                    ? {buttonLabel: pageWording.datatable.action.add.buttonLabel, onClick: () => openForm()}
                    : undefined}
                paginationOptions={{
                    page,
                    limit,
                    order,
                    orderBy,
                    total: tableData?.pagination?.total || 0,
                    rowsPerPageValues: [
                        {nb: defaultLimit / 2, isDefault: true},
                        {nb: defaultLimit, isDefault: true},
                        {nb: defaultLimit * 2, isDefault: false},
                        {nb: defaultLimit * 4, isDefault: false}
                    ],
                    meetNbRowsPerPage: false,
                    rowsPerPageLabel: strings.datatable.pagination.rowsPerPage,
                    onPaginationChange: (nextPage, nextLimit) => {setPage(nextPage); setLimit(nextLimit);},
                    onHandleSortChange: (nextOrder, nextOrderBy) => {setOrder(nextOrder); setOrderBy(nextOrderBy);},
                    onMultiCellsSearch: (newCells) => {
                        setPage(1);
                        setHeadCells(newCells);
                        refreshRecords(newCells);
                    }
                }}
            />
            <EditForm
                key={`${recordToEdit?.type ?? EditMode.Add}-${recordToEdit?.record.id ?? "new"}`}
                constraints={constraints}
                record={recordToEdit}
                modalOptions={modalOptions}
            />
        </Page>
    );
};
